import React from "react";
import { motion, AnimatePresence } from "framer-motion";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

import LeadFormHero from "./components/LeadFormHero";
import LeadFormSuccess from "./components/LeadFormSuccess";
import QuickCaptureForm from "./components/QuickCaptureForm";
import OptionalDetailsForm from "./components/OptionalDetailsForm";
import useLeadForm from "./hooks/useLeadForm";

const fadeVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

export default function LeadForm() {
  const theme = useTheme();
  const {
    screen,
    quickData,
    detailsData,
    errors,
    submitting,
    submitError,
    updateQuickField,
    updateDetailsField,
    handleQuickSubmit,
    handleDetailsSubmit,
    handleSkip,
  } = useLeadForm();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <LeadFormHero />

        <Container
          maxWidth="sm"
          sx={{ mt: { xs: -6, md: -10 }, pb: 8, position: "relative", zIndex: 2 }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: "12px",
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            }}
          >
            {/* Accent bar */}
            <Box
              sx={{
                height: 4,
                background:
                  "linear-gradient(90deg, #2563eb 0%, #10b981 100%)",
              }}
            />

            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <AnimatePresence mode="wait">
                {screen === "capture" && (
                  <motion.div
                    key="capture"
                    variants={fadeVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                  >
                    <QuickCaptureForm
                      quickData={quickData}
                      errors={errors}
                      submitting={submitting}
                      submitError={submitError}
                      updateQuickField={updateQuickField}
                      onSubmit={handleQuickSubmit}
                    />
                  </motion.div>
                )}

                {screen === "details" && (
                  <motion.div
                    key="details"
                    variants={fadeVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                  >
                    <OptionalDetailsForm
                      detailsData={detailsData}
                      errors={errors}
                      submitting={submitting}
                      submitError={submitError}
                      updateDetailsField={updateDetailsField}
                      onSubmit={handleDetailsSubmit}
                      onSkip={handleSkip}
                    />
                  </motion.div>
                )}

                {screen === "success" && (
                  <motion.div
                    key="success"
                    variants={fadeVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                  >
                    <LeadFormSuccess />
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </Paper>

          {screen === "capture" && (
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              display="block"
              sx={{ mt: 2 }}
            >
              Seus dados estão protegidos conforme a LGPD (Lei 13.709/2018)
            </Typography>
          )}
        </Container>
      </Box>
    </motion.div>
  );
}
